/**
 * Decode the payload of a JWT token (no signature verification: the claims are only used as
 * hints for the UI, the server re-validates the token on every call).
 * @param token - The JWT token string
 * @returns The payload object, or null if the token is malformed
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
    if (!base64) return null;

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    const payload: unknown = JSON.parse(jsonPayload);
    return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Read a single claim from a JWT token as a string.
 * @param token - The JWT token string
 * @param claim - Claim name (e.g. "fileViewEncrypted")
 * @returns The claim value as string, or null if missing or the token is invalid
 */
export function getJwtClaim(token: string, claim: string): string | null {
  const payload = decodeJwtPayload(token);
  const value = payload?.[claim];
  if (value === undefined || value === null) return null;
  return String(value);
}

/** JWT claim set by `GET /myfiles/btoken` for OTP / protected-file sessions. */
export const FILE_ACCESS_SCOPE_CLAIM = 'FileAccessScope';

/**
 * True when the bearer is a file-scoped viewer session (one file, read-only, no rtoken).
 * Claim presence is the signal; the client never chooses or changes the scope.
 */
export function hasFileAccessScope(token: string | null | undefined): boolean {
  if (!token) return false;
  const scope = getJwtClaim(token, FILE_ACCESS_SCOPE_CLAIM);
  return scope !== null && scope !== '';
}

/**
 * Decode a JWT token and extract its expiration timestamp
 * @param token - The JWT token string
 * @returns Unix timestamp in seconds, or null if invalid
 */
export function getJwtExpiration(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  // 'exp' is in seconds (JWT standard)
  return typeof exp === 'number' && exp > 0 ? exp : null;
}

/**
 * Check if a JWT token is expired
 * @param token - The JWT token string
 * @returns true if expired or invalid, false otherwise
 */
export function isJwtExpired(token: string): boolean {
  const exp = getJwtExpiration(token);
  if (!exp) return true;

  const now = Math.floor(Date.now() / 1000);
  return now >= exp;
}

/**
 * Check if a JWT token will expire soon
 * @param token - The JWT token string
 * @param thresholdSeconds - Number of seconds before expiry to consider "expiring soon" (default: 5 minutes)
 * @returns true if expiring within threshold, false otherwise
 */
export function isJwtExpiringSoon(token: string, thresholdSeconds: number = 5 * 60): boolean {
  const exp = getJwtExpiration(token);
  if (!exp) return true;

  const now = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = exp - now;

  return timeUntilExpiry < thresholdSeconds;
}
